import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import {
  PhotoIcon,
  VideoCameraIcon,
  CloudArrowUpIcon,
  LinkIcon,
  XMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface MediaUploaderProps {
  onImageUpload?: (url: string) => void;
  onThumbnailUpload?: (url: string) => void;
  onVideoUpload?: (url: string) => void;
  imageUrl?: string;
  thumbnailUrl?: string;
  videoUrl?: string;
}

interface MediaItem {
  type: 'image' | 'thumbnail' | 'video';
  url: string;
  file?: File;
  uploading?: boolean;
  error?: string;
}

const MediaUploader = forwardRef<any, MediaUploaderProps>(({
  onImageUpload,
  onThumbnailUpload,
  onVideoUpload,
  imageUrl,
  thumbnailUrl,
  videoUrl,
}, ref) => {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [activeTab, setActiveTab] = useState<'upload' | 'url'>('url');
  const [urlInput, setUrlInput] = useState('');
  const [selectedType, setSelectedType] = useState<'image' | 'thumbnail' | 'video'>('image');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize media from props
  useEffect(() => {
    const initialMedia: MediaItem[] = [];
    if (imageUrl) {
      initialMedia.push({ type: 'image', url: imageUrl });
    }
    if (thumbnailUrl) {
      initialMedia.push({ type: 'thumbnail', url: thumbnailUrl });
    }
    if (videoUrl) {
      initialMedia.push({ type: 'video', url: videoUrl });
    }
    if (initialMedia.length > 0) {
      setMedia(initialMedia);
    }
  }, [imageUrl, thumbnailUrl, videoUrl]);

  // Expose clear method to parent
  useImperativeHandle(ref, () => ({
    clearMedia: () => {
      setMedia([]);
      setUrlInput('');
      setSelectedType('image');
      setActiveTab('url');
      // Clear the callbacks as well
      onImageUpload?.('');
      onThumbnailUpload?.('');
      onVideoUpload?.('');
    }
  }));

  const handleUrlAdd = () => {
    if (!urlInput) return;

    const newMedia: MediaItem = {
      type: selectedType,
      url: urlInput,
    };

    setMedia([...media, newMedia]);
    
    // Call appropriate callback
    if (selectedType === 'image') onImageUpload?.(urlInput);
    else if (selectedType === 'thumbnail') onThumbnailUpload?.(urlInput);
    else if (selectedType === 'video') onVideoUpload?.(urlInput);

    setUrlInput('');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      alert('Please select an image or video file');
      return;
    }

    // Validate file size (max 10MB for images, 50MB for videos)
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File size exceeds ${isVideo ? '50MB' : '10MB'} limit`);
      return;
    }

    // Create preview URL
    const url = URL.createObjectURL(file);
    const type = isVideo ? 'video' : selectedType === 'thumbnail' ? 'thumbnail' : 'image';

    const newMedia: MediaItem = {
      type,
      url,
      file,
      uploading: true,
    };

    setMedia([...media, newMedia]);

    // Simulate upload (in real app, this would upload to server)
    setTimeout(() => {
      const uploadedUrl = `https://storage.example.com/${file.name}`;
      setMedia(prev => prev.map(m => 
        m.url === url ? { ...m, url: uploadedUrl, uploading: false } : m
      ));
      
      if (type === 'image') onImageUpload?.(uploadedUrl);
      else if (type === 'thumbnail') onThumbnailUpload?.(uploadedUrl);
      else if (type === 'video') onVideoUpload?.(uploadedUrl);
    }, 2000);
  };

  const removeMedia = (url: string) => {
    const itemToRemove = media.find(m => m.url === url);
    if (itemToRemove) {
      // Clear the corresponding form value
      if (itemToRemove.type === 'image') onImageUpload?.('');
      else if (itemToRemove.type === 'thumbnail') onThumbnailUpload?.('');
      else if (itemToRemove.type === 'video') onVideoUpload?.('');
    }
    setMedia(media.filter(m => m.url !== url));
  };

  const getMediaIcon = (type: string) => {
    switch (type) {
      case 'video': return VideoCameraIcon;
      case 'thumbnail':
      case 'image':
      default: return PhotoIcon;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Media Assets
        </h3>

        {/* Tab Switcher */}
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('url')}
            className={`flex-1 px-3 py-2 sm:px-4 rounded-lg font-medium text-sm sm:text-base transition-colors ${
              activeTab === 'url'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <LinkIcon className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
            URL
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('upload')}
            className={`flex-1 px-3 py-2 sm:px-4 rounded-lg font-medium text-sm sm:text-base transition-colors ${
              activeTab === 'upload'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <CloudArrowUpIcon className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
            Upload
          </button>
        </div>

        {/* Media Type Selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Media Type
          </label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as 'image' | 'thumbnail' | 'video')}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="image">Main Image</option>
            <option value="thumbnail">Thumbnail</option>
            <option value="video">Video</option>
          </select>
        </div>

        {/* URL Input */}
        {activeTab === 'url' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Media URL
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  className="flex-1 px-3 py-2 sm:px-4 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    selectedType === 'video' 
                      ? 'https://example.com/video.mp4'
                      : 'https://example.com/image.jpg'
                  }
                />
                <button
                  type="button"
                  onClick={handleUrlAdd}
                  disabled={!urlInput}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {/* File Upload */}
        {activeTab === 'upload' && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept={selectedType === 'video' ? 'video/*' : 'image/*'}
              onChange={handleFileSelect}
              className="hidden"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 sm:p-8 text-center cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
            >
              <CloudArrowUpIcon className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-400 mb-3 sm:mb-4" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {selectedType === 'video' 
                  ? 'MP4, WebM, OGG (max 50MB)'
                  : 'PNG, JPG, GIF, WebP (max 10MB)'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Media Gallery */}
      {media.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Added Media
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {media.map((item, index) => {
              const Icon = getMediaIcon(item.type);
              return (
                <div
                  key={index}
                  className="relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden group"
                >
                  {item.type === 'video' ? (
                    <div className="aspect-video bg-gray-900 flex items-center justify-center">
                      <VideoCameraIcon className="w-12 h-12 text-gray-600" />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gray-200 dark:bg-gray-700">
                      <img
                        src={item.url}
                        alt="Media preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    {item.uploading ? (
                      <div className="text-white text-sm">Uploading...</div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeMedia(item.url)}
                        className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  {/* Type Badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      item.type === 'image' ? 'bg-blue-100 text-blue-800' :
                      item.type === 'thumbnail' ? 'bg-green-100 text-green-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {item.type}
                    </span>
                  </div>

                  {/* Status Icon */}
                  {!item.uploading && (
                    <div className="absolute top-2 right-2">
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-2">
          Media Tips
        </h4>
        <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1">
          <li>• Main image appears as the hero image in the notification</li>
          <li>• Thumbnail is used in notification lists and previews</li>
          <li>• Videos will play inline on supported devices</li>
          <li>• Use 16:9 aspect ratio for best results</li>
          <li>• Optimize images for mobile (under 500KB recommended)</li>
        </ul>
      </div>
    </div>
  );
});

MediaUploader.displayName = 'MediaUploader';

export default MediaUploader;